import {
  FloatingFeature,
  FeatureBase,
} from "@models";
import {
  UpgradableNumber,
  ActionLimitation,
  CreatureLimitation,
} from "@dndModels";
import { CreatureType, isCreatureType, SizeType } from "@dndModels/Types";

// Adjust this import path to wherever TransformOption.ts actually lives.
import {
  PairedUGN,
  TransformStats,
  TransformAttributes,
} from "./TransformOption";

/**
 * Modes available to fields that are basically "pick one option" (enums,
 * limitation objects, from-char/from-creature selections). Doesn't make
 * sense to "Add" a selection, so it's just Leave or Replace.
 */
export type LeaveReplace = "Leave" | "Replace";

/**
 * Modes available to fields that are lists or composite objects where
 * "on top of" is meaningful (tags, gift ids, action limits, features).
 */
export type LeaveReplaceAdd = "Leave" | "Replace" | "Add";

/**
 * Modes available to fields that are ultimately numeric (UpgradableNumber
 * or PairedUGN based), where comparing magnitudes makes sense.
 */
export type NumberEnhancementMode = "Leave" | "Replace" | "Replace if Greater" | "Add";

// ---------------------------------------------------------------------------
// Generic-ish wrapper classes. TS doesn't make truly generic clone/toDBObj
// pairs easy without more infrastructure, so these are written explicitly,
// matching the style of the rest of the model classes.
// ---------------------------------------------------------------------------

/** Wraps a single UpgradableNumber (extra_pb, extra_cr, extra_max_hp, extra_init, extra_ac). */
export class NumberEnhancement {
  mode: NumberEnhancementMode = "Leave";
  value = new UpgradableNumber();

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? new UpgradableNumber(obj.value) : new UpgradableNumber();
    }
  }

  toDBObj(): any {
    return {
      mode: this.mode,
      value: this.value.toDBObj(),
    };
  }

  copy(copyMe: NumberEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.clone();
  }

  clone(): NumberEnhancement {
    const new_obj = new NumberEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps a PairedUGN (temp_hit_points, each ability score in TransformStats). */
export class PairedNumberEnhancement {
  mode: NumberEnhancementMode = "Leave";
  value = new PairedUGN();

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? new PairedUGN(obj.value) : new PairedUGN();
    }
  }

  toDBObj(): any {
    return {
      mode: this.mode,
      value: this.value.toDBObj(),
    };
  }

  copy(copyMe: PairedNumberEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.clone();
  }

  clone(): PairedNumberEnhancement {
    const new_obj = new PairedNumberEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * Wraps a from_char/from_creature selection (pb, cr, max_hp, init, ac).
 * Leave/Replace only — "Add" doesn't mean anything for a binary choice.
 */
export class FromSourceEnhancement {
  mode: LeaveReplace = "Leave";
  from_char = true;
  from_creature = false;

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.from_char = obj.from_char !== undefined ? obj.from_char : true;
      this.from_creature = obj.from_creature !== undefined ? obj.from_creature : false;
    }
  }

  toDBObj(): any {
    return {
      mode: this.mode,
      from_char: this.from_char,
      from_creature: this.from_creature,
    };
  }

  copy(copyMe: FromSourceEnhancement): void {
    this.mode = copyMe.mode;
    this.from_char = copyMe.from_char;
    this.from_creature = copyMe.from_creature;
  }

  clone(): FromSourceEnhancement {
    const new_obj = new FromSourceEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps creature_type. */
export class CreatureTypeEnhancement {
  mode: LeaveReplace = "Leave";
  value: CreatureType | "Inherit from Target" | "Inherit from Creature" = "Inherit from Target";

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? obj.value : "Inherit from Target";
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value };
  }

  copy(copyMe: CreatureTypeEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value;
  }

  clone(): CreatureTypeEnhancement {
    const new_obj = new CreatureTypeEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps swarm. */
export class SwarmEnhancement {
  mode: LeaveReplace = "Leave";
  value: "Yes" | "No" | "Inherit from Target" | "Inherit from Creature" = "Inherit from Target";

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? obj.value : "Inherit from Target";
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value };
  }

  copy(copyMe: SwarmEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value;
  }

  clone(): SwarmEnhancement {
    const new_obj = new SwarmEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps creature_limit. */
export class CreatureLimitEnhancement {
  mode: LeaveReplace = "Leave";
  value = new CreatureLimitation();

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? new CreatureLimitation(obj.value) : new CreatureLimitation();
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value.toDBObj() };
  }

  copy(copyMe: CreatureLimitEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.clone();
  }

  clone(): CreatureLimitEnhancement {
    const new_obj = new CreatureLimitEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps a string list (tags, built_in_gift_ids). */
export class StringListEnhancement {
  mode: LeaveReplaceAdd = "Leave";
  value: string[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? [...obj.value] : [];
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value };
  }

  copy(copyMe: StringListEnhancement): void {
    this.mode = copyMe.mode;
    this.value = [...copyMe.value];
  }

  clone(): StringListEnhancement {
    const new_obj = new StringListEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps char_action_limits / creature_action_limits. */
export class ActionLimitationEnhancement {
  mode: LeaveReplaceAdd = "Leave";
  value = new ActionLimitation();

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? new ActionLimitation(obj.value) : new ActionLimitation();
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value.toDBObj() };
  }

  copy(copyMe: ActionLimitationEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.clone();
  }

  clone(): ActionLimitationEnhancement {
    const new_obj = new ActionLimitationEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps fbs (the extra features a Transform grants). */
export class FeatureBaseListEnhancement {
  mode: LeaveReplaceAdd = "Leave";
  value: FeatureBase[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.value = obj.value ? obj.value.map((o: any) => new FeatureBase(o)) : [];
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value.map(o => o.toDBObj()) };
  }

  copy(copyMe: FeatureBaseListEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.map(o => o.clone());
  }

  clone(): FeatureBaseListEnhancement {
    const new_obj = new FeatureBaseListEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/** Wraps ability_scores — one PairedNumberEnhancement per ability. */
export class TransformStatsEnhancement {
  strength = new PairedNumberEnhancement();
  dexterity = new PairedNumberEnhancement();
  constitution = new PairedNumberEnhancement();
  intelligence = new PairedNumberEnhancement();
  wisdom = new PairedNumberEnhancement();
  charisma = new PairedNumberEnhancement();

  constructor(obj?: any) {
    if (obj) {
      this.strength = obj.strength ? new PairedNumberEnhancement(obj.strength) : new PairedNumberEnhancement();
      this.dexterity = obj.dexterity ? new PairedNumberEnhancement(obj.dexterity) : new PairedNumberEnhancement();
      this.constitution = obj.constitution ? new PairedNumberEnhancement(obj.constitution) : new PairedNumberEnhancement();
      this.intelligence = obj.intelligence ? new PairedNumberEnhancement(obj.intelligence) : new PairedNumberEnhancement();
      this.wisdom = obj.wisdom ? new PairedNumberEnhancement(obj.wisdom) : new PairedNumberEnhancement();
      this.charisma = obj.charisma ? new PairedNumberEnhancement(obj.charisma) : new PairedNumberEnhancement();
    }
  }

  toDBObj(): any {
    return {
      strength: this.strength.toDBObj(),
      dexterity: this.dexterity.toDBObj(),
      constitution: this.constitution.toDBObj(),
      intelligence: this.intelligence.toDBObj(),
      wisdom: this.wisdom.toDBObj(),
      charisma: this.charisma.toDBObj(),
    };
  }

  copy(copyMe: TransformStatsEnhancement): void {
    this.strength = copyMe.strength.clone();
    this.dexterity = copyMe.dexterity.clone();
    this.constitution = copyMe.constitution.clone();
    this.intelligence = copyMe.intelligence.clone();
    this.wisdom = copyMe.wisdom.clone();
    this.charisma = copyMe.charisma.clone();
  }

  clone(): TransformStatsEnhancement {
    const new_obj = new TransformStatsEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * Changes a Transform's attributes.
 * Replace swaps out all of them.
 * Add puts these on top, with any that are for the same attribute replacing the Transform's.
 */
export class TransformAttributesEnhancement {
  mode: LeaveReplaceAdd = "Leave";
  value = new TransformAttributes();

  constructor(obj?: any) {
    if (obj) {
      if (obj.mode) {
        this.mode = obj.mode;
        this.value = obj.value ? new TransformAttributes(obj.value) : new TransformAttributes();
      } else if (obj.attributes || obj.from_target || obj.from_creature) {
        // Old format, which had a separate mode for each of attributes, from_target, and from_creature.
        const modes: string[] = [obj.attributes?.mode, obj.from_target?.mode, obj.from_creature?.mode];
        this.mode = modes.includes("Replace") ? "Replace" : (modes.includes("Add") ? "Add" : "Leave");
        this.value = new TransformAttributes({
          attributes: obj.attributes?.value,
          from_target: obj.from_target?.value,
          from_creature: obj.from_creature?.value,
        });
      }
    }
  }

  toDBObj(): any {
    return { mode: this.mode, value: this.value.toDBObj() };
  }

  copy(copyMe: TransformAttributesEnhancement): void {
    this.mode = copyMe.mode;
    this.value = copyMe.value.clone();
  }

  clone(): TransformAttributesEnhancement {
    const new_obj = new TransformAttributesEnhancement();
    new_obj.copy(this);
    return new_obj;
  }
}

export class TransformUpgradeFeature extends FloatingFeature {
  transform_tag = "";

  creature_limit = new CreatureLimitEnhancement();
  creature_type?: CreatureType;
  swarm?: boolean;

  pb = new PairedNumberEnhancement();
  cr = new PairedNumberEnhancement();

  size = new PairedNumberEnhancement();

  max_hp = new PairedNumberEnhancement();
  temp_hit_points = new PairedNumberEnhancement();

  init = new PairedNumberEnhancement();
  ac = new PairedNumberEnhancement();

  ability_scores = new TransformStatsEnhancement();

  char_action_limits = new ActionLimitationEnhancement();

  // Extra features added on top of (or in place of) the Transform's own fbs.
  fbs = new FeatureBaseListEnhancement();

  built_in_gift_ids = new StringListEnhancement();

  attributes = new TransformAttributesEnhancement();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.transform_tag = obj.transform_tag ? obj.transform_tag : "";

      this.creature_limit = obj.creature_limit ? new CreatureLimitEnhancement(obj.creature_limit) : new CreatureLimitEnhancement();
      if (isCreatureType(obj.creature_type)) {
        this.creature_type = obj.creature_type;
      }
      if (typeof obj.swarm === "boolean") {
        this.swarm = obj.swarm;
      }

      this.pb = obj.pb ? new PairedNumberEnhancement(obj.pb) : new PairedNumberEnhancement();

      this.cr = obj.cr ? new PairedNumberEnhancement(obj.cr) : new PairedNumberEnhancement();

      this.size = obj.size ? new PairedNumberEnhancement(obj.size) : new PairedNumberEnhancement();

      this.max_hp = obj.max_hp ? new PairedNumberEnhancement(obj.max_hp) : new PairedNumberEnhancement();
      this.temp_hit_points = obj.temp_hit_points ? new PairedNumberEnhancement(obj.temp_hit_points) : new PairedNumberEnhancement();

      this.init = obj.init ? new PairedNumberEnhancement(obj.init) : new PairedNumberEnhancement();
      this.ac = obj.ac ? new PairedNumberEnhancement(obj.ac) : new PairedNumberEnhancement();

      this.ability_scores = obj.ability_scores ? new TransformStatsEnhancement(obj.ability_scores) : new TransformStatsEnhancement();

      this.char_action_limits = obj.char_action_limits ? new ActionLimitationEnhancement(obj.char_action_limits) : new ActionLimitationEnhancement();
     
      this.fbs = obj.fbs ? new FeatureBaseListEnhancement(obj.fbs) : new FeatureBaseListEnhancement();

      this.built_in_gift_ids = obj.built_in_gift_ids ? new StringListEnhancement(obj.built_in_gift_ids) : new StringListEnhancement();

      this.attributes = obj.attributes ? new TransformAttributesEnhancement(obj.attributes) : new TransformAttributesEnhancement();
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      transform_tag: this.transform_tag,

      creature_limit: this.creature_limit.toDBObj(),
      creature_type: this.creature_type,
      swarm: this.swarm,

      pb: this.pb.toDBObj(),

      cr: this.cr.toDBObj(),

      size: this.size.toDBObj(),

      max_hp: this.max_hp.toDBObj(),
      temp_hit_points: this.temp_hit_points.toDBObj(),

      init: this.init.toDBObj(),
      ac: this.ac.toDBObj(),

      ability_scores: this.ability_scores.toDBObj(),

      char_action_limits: this.char_action_limits.toDBObj(),

      fbs: this.fbs.toDBObj(),

      built_in_gift_ids: this.built_in_gift_ids.toDBObj(),

      attributes: this.attributes.toDBObj(),
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof TransformUpgradeFeature) {
      this.transform_tag = copyMe.transform_tag;

      this.creature_limit = copyMe.creature_limit.clone();
      this.creature_type = copyMe.creature_type;
      this.swarm = copyMe.swarm;

      this.pb = copyMe.pb.clone();

      this.cr = copyMe.cr.clone();

      this.size = copyMe.size.clone();

      this.max_hp = copyMe.max_hp.clone();
      this.temp_hit_points = copyMe.temp_hit_points.clone();

      this.init = copyMe.init.clone();
      this.ac = copyMe.ac.clone();

      this.ability_scores = copyMe.ability_scores.clone();

      this.char_action_limits = copyMe.char_action_limits.clone();

      this.fbs = copyMe.fbs.clone();

      this.built_in_gift_ids = copyMe.built_in_gift_ids.clone();

      this.attributes = copyMe.attributes.clone();
    }
  }

  clone(drop_ids = false): TransformUpgradeFeature {
    const clone = new TransformUpgradeFeature();
    clone.copy(this, drop_ids);
    return clone;
  }
}

export class OverridableNumber  {
  mode: NumberEnhancementMode = "Leave";
  num = new UpgradableNumber();

  constructor(obj?: any) {
    if (obj) {
      this.mode = obj.mode ? obj.mode : "Leave";
      this.num = obj.num !== undefined ? new UpgradableNumber(obj.num) : new UpgradableNumber();
    }
  }

  toDBObj(): any {
    return {
      mode: this.mode,
      num: this.num.toDBObj(),
    };
  }

  copy(copyMe: OverridableNumber): void {
    this.mode = copyMe.mode;
    this.num = copyMe.num.clone();
  }

  clone(): OverridableNumber {
    const new_obj = new OverridableNumber();
    new_obj.copy(this);
    return new_obj;
  }
}